import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DevProjectPicker } from "@/components/DevProjectPicker";
import { CalendarPlus, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = [
  ["OPEN_HOUSE", "Open House"], ["PROJECT_PREVIEW", "Project Preview"], ["PROJECT_BRIEFING", "Project Briefing"],
  ["PROJECT_LAUNCH", "Project Launch"], ["INVESTORS_NIGHT", "Investors' Night"], ["ACCREDITATION", "Accreditation"],
  ["PROPERTY_VIEWING", "Property Viewing"], ["SEMINAR_TRAINING", "Seminar / Training"], ["NETWORKING", "Networking"], ["OTHER", "Other"],
];
const toLocalInput = (iso) => {
  if (!iso) return "";
  try { const d = new Date(iso); const off = d.getTimezoneOffset(); const l = new Date(d.getTime() - off * 60000); return l.toISOString().slice(0, 16); } catch { return ""; }
};

export default function PostEvent() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("id");
  const [loading, setLoading] = useState(!!editId);
  const [busy, setBusy] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [dev, setDev] = useState({ id: null, name: null });
  const [proj, setProj] = useState({ id: null, name: null });
  const [f, setF] = useState({
    event_type: "OPEN_HOUSE", title: "", description: "", start_at: "", end_at: "", timezone: "Asia/Manila",
    event_format: "IN_PERSON", venue: "", city: "", province: "", meeting_link: "", visibility: "NETWORK_ONLY",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/events/${editId}`);
        setF({
          event_type: data.event_type, title: data.title || "", description: data.description || "",
          start_at: toLocalInput(data.start_at), end_at: toLocalInput(data.end_at), timezone: data.timezone || "Asia/Manila",
          event_format: data.event_format, venue: data.venue || "", city: data.city || "", province: data.province || "",
          meeting_link: data.meeting_link || "", visibility: data.visibility,
        });
        setDev({ id: data.developer_id || null, name: data.developer_name || null });
        setProj({ id: data.project_id || null, name: data.project_name || null });
        if (data.cover_image) setCoverPreview(data.cover_image);
      } catch (e) { toast.error(apiError(e)); nav("/my-events"); } finally { setLoading(false); }
    })();
  }, [editId, nav]);

  const onCover = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const save = async (publish) => {
    if (!f.title.trim()) { toast.error("Enter an event title."); return; }
    if (publish) {
      if (!f.start_at || !f.end_at) { toast.error("Set start and end date/time to publish."); return; }
      if (f.end_at <= f.start_at) { toast.error("End must be after start."); return; }
      if (["IN_PERSON", "HYBRID"].includes(f.event_format) && !f.venue.trim() && !f.city.trim()) { toast.error("Add a venue or city for in-person events."); return; }
    }
    setBusy(true);
    try {
      const body = {
        ...f, publish,
        developer_id: dev.id, developer_name: dev.name, project_id: proj.id, project_name: proj.name,
      };
      let ev;
      if (editId) { const { data } = await api.put(`/events/${editId}`, body); ev = data; }
      else { const { data } = await api.post("/events", body); ev = data; }
      if (coverFile) {
        const fd = new FormData(); fd.append("file", coverFile);
        try { await api.post(`/events/${ev.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e) { toast.error("Event saved, but cover upload failed: " + apiError(e)); }
      }
      toast.success(publish ? "Event published!" : "Draft saved");
      nav(publish ? `/event/${ev.id}` : "/my-events");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <PageShell testid="post-event-page"><p className="text-sm text-slate-400">Loading…</p></PageShell>;

  const online = ["ONLINE", "HYBRID"].includes(f.event_format);
  const inPerson = ["IN_PERSON", "HYBRID"].includes(f.event_format);

  return (
    <PageShell testid="post-event-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><CalendarPlus className="h-7 w-7 text-[#C79A3B]" /> {editId ? "Edit Event" : "Post an Event"}</h1>
        <p className="text-sm text-slate-500 mt-1">Open houses, project launches, briefings, seminars and more.</p>

        <div className="mt-7 bg-slate-50 border border-border rounded-2xl p-6 sm:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Event type</Label>
              <Select value={f.event_type} onValueChange={(v) => set("event_type", v)}>
                <SelectTrigger data-testid="event-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Format</Label>
              <Select value={f.event_format} onValueChange={(v) => set("event_format", v)}>
                <SelectTrigger data-testid="event-format" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN_PERSON">In Person</SelectItem>
                  <SelectItem value="ONLINE">Online</SelectItem>
                  <SelectItem value="HYBRID">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Title</Label><Input data-testid="event-title" value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" placeholder="e.g. Grand Open House — 2BR Condo, Makati" /></div>
          <div><Label>Description</Label><Textarea data-testid="event-description" value={f.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1.5" /></div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Starts</Label><Input data-testid="event-start" type="datetime-local" value={f.start_at} onChange={(e) => set("start_at", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Ends</Label><Input data-testid="event-end" type="datetime-local" value={f.end_at} onChange={(e) => set("end_at", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Timezone</Label><Input data-testid="event-timezone" value={f.timezone} onChange={(e) => set("timezone", e.target.value)} className="mt-1.5" /></div>

          {inPerson && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>Venue</Label><Input data-testid="event-venue" value={f.venue} onChange={(e) => set("venue", e.target.value)} className="mt-1.5" placeholder="Building / address" /></div>
              <div><Label>City</Label><Input data-testid="event-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Province <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="event-province" value={f.province} onChange={(e) => set("province", e.target.value)} className="mt-1.5" /></div>
            </div>
          )}
          {online && (
            <div><Label>Meeting link <span className="text-slate-400 font-normal">(shared only with confirmed RSVPs)</span></Label>
              <Input data-testid="event-meeting-link" value={f.meeting_link} onChange={(e) => set("meeting_link", e.target.value)} className="mt-1.5" placeholder="https://…" /></div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Developer <span className="text-slate-400 font-normal">(optional)</span></Label>
              <DevProjectPicker kind="developer" value={dev} onChange={(v) => { setDev(v); setProj({ id: null, name: null }); }} testid="event-developer" /></div>
            <div><Label>Project <span className="text-slate-400 font-normal">(optional)</span></Label>
              <DevProjectPicker kind="project" value={proj} onChange={setProj} developerId={dev.id} testid="event-project" /></div>
          </div>

          <div><Label>Visibility</Label>
            <Select value={f.visibility} onValueChange={(v) => set("visibility", v)}>
              <SelectTrigger data-testid="event-visibility" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NETWORK_ONLY">Network Only — registered PropertyConnect users</SelectItem>
                <SelectItem value="PUBLIC">Public — visible in the public marketplace</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div><Label>Cover image <span className="text-slate-400 font-normal">(optional)</span></Label>
            <label className="mt-1.5 flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white p-3 cursor-pointer hover:border-primary" data-testid="event-cover-label">
              <ImagePlus className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-500">{coverFile ? coverFile.name : "Upload a cover image (JPG/PNG/WEBP)"}</span>
              <input type="file" accept="image/*" onChange={onCover} className="hidden" data-testid="event-cover-input" />
            </label>
            {coverPreview && <img src={coverPreview} alt="cover preview" className="mt-3 h-40 w-full object-cover rounded-xl" data-testid="event-cover-preview" />}
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button data-testid="event-save-draft" onClick={() => save(false)} disabled={busy} variant="outline" className="rounded-xl border-[#0F2C59] text-[#0F2C59]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Draft"}
            </Button>
            <Button data-testid="event-publish" onClick={() => save(true)} disabled={busy} className="bg-[#C79A3B] text-[#0F2C59] hover:bg-[#C79A3B]/90 font-semibold rounded-xl">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Save & Publish" : "Publish Event")}
            </Button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
